// packages\shared-types\src\types\keyboard\advancedKey.ts
import type { KeyPosition } from "./commonType";

// 高级键模式枚举
export enum HigherKeyMode {
  NONE = 0, // 无高级键
  DKS = 1, // 动态键程
  MPT = 2, // 多点触发
  MT = 3, // 模式切换/触发延迟
  TGL = 4, // 触发延迟
  END = 5, // 结束触发
  SOCD = 6, // 同时按下冲突解决
  RS = 7, // 迅洁
  CB = 8, // 组合键（华奋达）
  SNAPKEY = 9, // SnapKey（容园）
  MDS = 10, // MDS（容园）
}

// 基础响应类型
export interface HigherKeyBaseResponse extends KeyPosition {
  mode: HigherKeyMode; // 高级键类型
}

// 获取高级键类型
export type GetHigherKeyParType = KeyPosition;
export type GetHigherKeyResType = HigherKeyBaseResponse;

// 删除高级键
export interface SetHigherKeyNONEParType extends KeyPosition {
  mode: HigherKeyMode.NONE;
  data: {
    mode: 0; // 设置为 0 表示删除高级键
  };
}

// DKS (动态键程) 相关类型
export interface DKSData {
  kcs: number[]; // DKS各层级的键值数组，长度为4
  trps: number[]; // DKS各层级的触发点行程参数数组，长度为4
  dbs: number[]; // 星闪长度2；华奋达磁轴长度4：[起始, 触底, 触底抬起, 完全抬起]，单位 mm
}

export interface SetHigherKeyDKSParType extends KeyPosition {
  data: DKSData;
}

export interface SetHigherKeyDKSResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.DKS;
  data: DKSData;
}

// MPT (多点触发) 相关类型
export interface MPTData {
  kcs: number[]; // MPT各层级的键值数组，长度为3
  dbs: number[]; // MPT的死区设置数组，长度为3，单位为mm
}

export interface SetHigherKeyMPTParType extends KeyPosition {
  data: MPTData;
}

export interface SetHigherKeyMPTResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.MPT;
  data: MPTData;
}

// MT (模式切换/触发延迟) 相关类型
export interface MTData {
  kcs: number[]; // MT各层级的键值数组，长度为2
  time: number; // 延迟时间，单位为ms
}

export interface SetHigherKeyMTParType extends KeyPosition {
  mode: "MT"; // 设置为 'MT' 表示设置MT高级键
  data: {
    kcs: number[]; // MPT各层级的键值数组，长度为3 (注意: 请求和响应数据结构不同)
    time: number; // 延迟时间，单位为ms
  };
}

export interface SetHigherKeyMTResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.MT;
  data: MTData;
}

// TGL (触发延迟) 相关类型
export interface TGLData {
  kcs: number; // TGL的键值
  delay: number; // 延迟时间，单位为ms
}

export interface SetHigherKeyTGLParType extends KeyPosition {
  mode: "TGL"; // 设置为 'TGL' 表示设置TGL高级键
  data: TGLData;
}

export interface SetHigherKeyTGLResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.TGL;
  data: TGLData;
}

// END (结束触发) 相关类型
export interface ENDData {
  kcs: number[]; // END的键值数组，长度为2
  delay: number; // 延迟时间，单位为ms
}

export interface SetHigherKeyENDParType extends KeyPosition {
  mode: "END"; // 设置为 'END' 表示设置END高级键
  data: ENDData;
}

export interface SetHigherKeyENDResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.END;
  data: ENDData;
}

// SOCD (同时按下冲突解决) 相关类型
export interface SOCDData {
  row2: number; // 按键在键盘矩阵中的行号2
  col2: number; // 按键在键盘矩阵中的列号2
  socdMode: number; // SOCD模式：0=后覆盖，1=前优先，2=后优先，3=中性
  kcs: number[]; // SOCD的键值数组，长度为2
  delay: number; // 延迟时间，单位为ms
}

export interface SetHigherKeySOCDParType extends KeyPosition {
  row2: number;
  col2: number;
  mode: "SOCD"; // 设置为 'SOCD' 表示设置SOCD高级键
  socdMode: number; // SOCD模式：0=后覆盖，1=前优先，2=后优先，3=中性
  kcs: number[];
  delay: number;
}

export interface SetHigherKeySOCDResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.SOCD;
  data: SOCDData;
}

// RS (迅洁) 相关类型
export interface RSData {
  row2: number; // 按键在键盘矩阵中的行号2
  col2: number; // 按键在键盘矩阵中的列号2
  kcs: number[]; // 键值数组，长度为2，对应两个按键的键值
  delay: number; // 延迟时间，单位为ms
}

export interface SetHigherKeyRSParType extends KeyPosition {
  mode: "RS"; // 固定为 'RS'，表示设置RS高级键
  data: RSData;
}

export interface SetHigherKeyRSResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.RS;
  data: RSData;
}

// CB（组合键，华奋达）：热键一、热键二为修饰键，常规键为普通键
export interface CBData {
  /** [热键一, 热键二, 常规按键] 的键值编码 */
  kcs: [number, number, number];
}

export interface SetHigherKeyCBParType extends KeyPosition {
  mode: "CB";
  data: CBData;
}

export interface SetHigherKeyCBResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.CB;
  data: CBData;
}

// SnapKey（容园，双键结构同 RS）
export type SnapKeyData = RSData;

export interface SetHigherKeySnapKeyParType extends KeyPosition {
  mode: "SNAPKEY";
  data: SnapKeyData;
}

export interface SetHigherKeySnapKeyResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.SNAPKEY;
  data: SnapKeyData;
}

// MDS（容园，双键结构同 RS）
export type MDSData = RSData;

export interface SetHigherKeyMDSParType extends KeyPosition {
  mode: "MDS";
  data: MDSData;
}

export interface SetHigherKeyMDSResType extends HigherKeyBaseResponse {
  mode: HigherKeyMode.MDS;
  data: MDSData;
}
